import { useState, useCallback, useEffect } from "react";
import {
  getSubject,
  getBody,
  getAttachmentItems,
  getAttachmentContent,
  removeAttachment,
  addBase64Attachment,
  setSubject,
  setBody,
  getUserEmail,
  getRecipientsWithCc,
  getEmlBase64,
} from "../utils/officeItemHelpers";
import {
  isBlockedAttachment,
  getFileExtension,
} from "../utils/blockedextensions-helper";
import { useI18n } from "../contexts/I18nContext";

// localStorage — read by both taskpane and commands.js (separate context)
const LS_MAIL_PREPARED = "avamboo_mail_prepared";
const SS_ORIGINAL_DRAFT = "avamboo_original_draft";

async function _fetchLogoBase64() {
  try {
    const resp = await fetch("/assets/secured-by-ae.png");
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * usePrepareEncryption
 *
 * Implements the "Verschlüsselung vorbereiten" workflow.
 * Mutates the compose item (subject, body, attachments) without calling saveCompose().
 * Sets a localStorage flag so commands.js can allow the Send immediately.
 *
 * @param {object} params
 * @param {object} params.pdfEngine     - PdfEngineClient instance
 * @param {object|null} params.settings - BenutzerSettings
 * @param {boolean} params.isPaid       - License type
 */
export function usePrepareEncryption({ pdfEngine, settings, isPaid, onWarning }) {
  const { t } = useI18n();
  const [isPrepared, setIsPrepared] = useState(
    // True only if a restorable snapshot exists in this session.
    // localStorage is kept solely for commands.js (separate execution context).
    () => sessionStorage.getItem(SS_ORIGINAL_DRAFT) !== null,
  );
  const [isPreparing, setIsPreparing] = useState(false);
  const [preparedInSeconds, setPreparedInSeconds] = useState(null);

  // On mount: if localStorage has the prepared flag but no snapshot exists in
  // sessionStorage, the flag is stale — it belongs to a previous compose session
  // that was closed or sent without going through restoreDraft. Clear it so the
  // UI starts fresh for the new compose item.
  useEffect(() => {
    if (
      localStorage.getItem(LS_MAIL_PREPARED) === "1" &&
      sessionStorage.getItem(SS_ORIGINAL_DRAFT) === null
    ) {
      localStorage.removeItem(LS_MAIL_PREPARED);
      setIsPrepared(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Internal: prepare full container PDF (no saveAndClose) ────────────────

  const _prepareFull = useCallback(
    async (password, keepSubjectUnencrypted) => {
      const defaultBodyHtml =
        `<p>${t("Diese E-Mail wurde mit <strong>Avamboo Encrypt</strong> verschlüsselt.")}</p>` +
        `<p>${t("Bitte öffnen Sie den Anhang mit dem Adobe Acrobat Reader und geben Sie das vereinbarte Passwort ein.")}</p>`;
      // [TIMING] Temporary performance measurement — remove after benchmarking
      const _t0 = performance.now();
      const _lap = (label) => console.log(`[TIMING] ${label}: ${(performance.now() - _t0).toFixed(0)}ms`);

      // 1. Collect mail data
      const [subject, bodyHtml, recipientsByCc, allItems] = await Promise.all([
        getSubject(),
        getBody(),
        getRecipientsWithCc(),
        getAttachmentItems(),
      ]);

      const attachmentItems = allItems.filter((a) => !a.isInline);

      const toRecipients = recipientsByCc.to
        .filter((r) => r.emailAddress)
        .map((r) => r.emailAddress);

      const ccRecipients = recipientsByCc.cc
        .filter((r) => r.emailAddress)
        .map((r) => r.emailAddress);

      // 2. Attachment count and size validation (must match WASM hard limits)
      const MAX_ATTACHMENT_COUNT = 5;
      const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5 MB

      if (attachmentItems.length > MAX_ATTACHMENT_COUNT) {
        throw Object.assign(
          new Error(t("Zu viele Anhänge. Maximal {count} Anhänge sind erlaubt.", { count: MAX_ATTACHMENT_COUNT })),
          { code: "TOO_MANY_ATTACHMENTS" },
        );
      }

      const totalBytes = attachmentItems.reduce((sum, a) => sum + (a.size ?? 0), 0);
      if (totalBytes > MAX_ATTACHMENT_BYTES) {
        const totalMB = (totalBytes / 1024 / 1024).toFixed(1);
        throw Object.assign(
          new Error(t("Die Anhänge sind zu groß ({totalMB} MB). Das Maximum beträgt 5 MB.", { totalMB })),
          { code: "ATTACHMENTS_TOO_LARGE" },
        );
      }

      // 3. Blocked extension check
      for (const item of attachmentItems) {
        if (isBlockedAttachment(item.name)) {
          const ext = getFileExtension(item.name);
          const confirmed = window.confirm(
            t("Die Datei {name} kann vom Empfänger möglicherweise nicht geöffnet werden.\nDer Adobe Acrobat unterstützt das Öffnen von Dateien mit der Endung {ext} nicht.\nMöchten Sie fortfahren?", {
              name: item.name,
              ext,
            }),
          );
          if (!confirmed) {
            throw Object.assign(new Error("USER_ABORTED"), { code: "USER_ABORTED" });
          }
        }
      }

      // [TIMING] log inputs for benchmark table
      const totalSizeKB = (attachmentItems.reduce((s, a) => s + (a.size ?? 0), 0) / 1024).toFixed(0);
      console.log(`[TIMING] START — attachments: ${attachmentItems.length}, total size: ${totalSizeKB} KB`);
      _lap("after collect+validate");

      // 3. Fetch attachment content
      const attachments = [];
      for (const item of attachmentItems) {
        const { content } = await getAttachmentContent(item.id);
        attachments.push({
          fileName: item.name,
          contentType: "application/octet-stream",
          base64: content,
        });
      }
      _lap("after fetch attachments");

      // 4. EML (soft-fail)
      const includeEml = settings?.originalEmailEinbetten ?? false;
      let emlBase64 = null;
      if (includeEml) {
        try {
          emlBase64 = await getEmlBase64();
        } catch (emlErr) {
          console.warn("[usePrepareEncryption] EML build failed (soft-fail):", emlErr);
          onWarning?.(t("Die Original-E-Mail konnte nicht eingebettet werden. Die Verschlüsselung wird ohne EML-Anhang fortgesetzt."));
        }
      }
      _lap("after EML");

      // 5. Build meta
      const meta = {
        subject,
        sender: getUserEmail(),
        to: toRecipients.join(", "),
        cc: ccRecipients.join(", "),
        timestamp: new Date().toISOString(),
      };

      // 6. Strip HTML tags from body
      const bodyPlainText = bodyHtml
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<\/div>/gi, "\n")
        .replace(/<\/li>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

      // 7. Call engine
      let result;
      try {
        result = await pdfEngine.buildContainerAndEncrypt({
          meta,
          bodyText: bodyPlainText,
          attachments,
          emlBase64,
          password,
          isPaid: !!isPaid,
        });
      } catch (engineErr) {
        console.error("[usePrepareEncryption] PDF engine crash:", engineErr);
        throw Object.assign(
          new Error(t("Die PDF-Engine ist abgestürzt. Bitte laden Sie das Add-in neu und versuchen Sie es erneut.")),
          { code: "PDF_ENGINE_CRASH" }
        );
      }
      _lap("after WASM engine");

      if (result.errors?.length > 0) {
        console.warn("[usePrepareEncryption] Engine non-fatal issues:", result.errors);
      }

      // 8. Determine outgoing subject
      const finalSubject = keepSubjectUnencrypted
        ? subject
        : (settings?.vorlageBetreff ?? t("Verschlüsselte E-Mail"));

      // 9. Determine outgoing body + append logo
      const baseBody = settings?.vorlageBodyHtml ?? defaultBodyHtml;
      const logoDataUrl = await _fetchLogoBase64();
      const logoHtml = logoDataUrl
        ? `<br/><img src="${logoDataUrl}" alt="Secured by Avamboo Encrypt" style="width:200px;height:auto;display:block;margin-top:16px;" />`
        : "";
      const finalBody = baseBody + logoHtml;

      // 10. Remove all non-inline attachments
      for (const item of attachmentItems) {
        await removeAttachment(item.id);
      }

      // 11. Set new subject and body
      await setSubject(finalSubject);
      await setBody(finalBody);

      // 12. Attach encrypted container PDF
      await addBase64Attachment(result.data, t("Verschluesselte-Mail.pdf"), false);
      _lap("TOTAL (end)");
    },
    [pdfEngine, settings, isPaid, onWarning, t],
  );

  // ── Internal: prepare PDF-only encryption (no saveAndClose) ───────────────

  const _preparePdfOnly = useCallback(
    async (password) => {
      const allItems = await getAttachmentItems();
      const pdfItems = allItems.filter(
        (a) => !a.isInline && a.name?.toLowerCase().endsWith(".pdf"),
      );

      const MAX_PDF_COUNT = 10;
      const MAX_PDF_BYTES = 25 * 1024 * 1024; // 25 MB

      if (pdfItems.length > MAX_PDF_COUNT) {
        const err = new Error(
          t("Zu viele PDF-Anhänge ({current}). Maximal {max} PDF-Anhänge sind erlaubt.", {
            current: pdfItems.length,
            max: MAX_PDF_COUNT,
          }),
        );
        err.code = "TOO_MANY_ATTACHMENTS";
        throw err;
      }

      const totalBytes = pdfItems.reduce((sum, a) => sum + (a.size ?? 0), 0);
      if (totalBytes > MAX_PDF_BYTES) {
        const totalMB = (totalBytes / (1024 * 1024)).toFixed(1);
        const err = new Error(
          t("Die PDF-Anhänge sind zu groß ({totalMB} MB). Das Maximum beträgt 25 MB.", { totalMB }),
        );
        err.code = "ATTACHMENTS_TOO_LARGE";
        throw err;
      }

      const skipped = [];
      const encrypted = [];

      for (const item of pdfItems) {
        try {
          const { content } = await getAttachmentContent(item.id);
          const result = await pdfEngine.encryptPdf(content, password);
          encrypted.push({ id: item.id, name: item.name, base64: result.data });
        } catch (err) {
          if (err.code === "PDF_ALREADY_ENCRYPTED") {
            skipped.push(item.name);
            continue;
          }
          window.alert(
            t("Fehler beim Verschlüsseln\n\nDie PDF-Datei \"{name}\" konnte nicht verschlüsselt werden.", { name: item.name }),
          );
          throw err;
        }
      }

      if (skipped.length > 0) {
        const msg =
          skipped.length === 1
            ? t("Ein PDF-Anhang dieser E-Mail ist bereits passwortgeschützt und wird nicht erneut verschlüsselt.")
            : t("{count} PDF-Anhänge dieser E-Mail sind bereits passwortgeschützt und werden nicht erneut verschlüsselt.", { count: skipped.length });
        window.alert(msg);
      }

      for (const enc of encrypted) {
        await removeAttachment(enc.id);
        await addBase64Attachment(enc.base64, enc.name, false);
      }
    },
    [pdfEngine, t],
  );

  // ── Public: prepareEncryption ──────────────────────────────────────────────

  /**
   * Saves a snapshot of the current draft to sessionStorage, then mutates the
   * compose item (subject, body, attachments) with the encrypted version.
   * Does NOT call saveCompose(). Sets localStorage flag when done.
   *
   * @param {{ password: string, mode: "full"|"pdfOnly", keepSubjectUnencrypted: boolean }} params
   */
  const prepareEncryption = useCallback(
    async ({ password, mode, keepSubjectUnencrypted }) => {
      setIsPreparing(true);
      const startTime = Date.now();
      try {
        // 1. Capture snapshot before any mutations
        const [subject, bodyHtml, allItems] = await Promise.all([
          getSubject(),
          getBody(),
          getAttachmentItems(),
        ]);
        const attachmentNames = allItems
          .filter((a) => !a.isInline)
          .map((a) => a.name);

        sessionStorage.setItem(
          SS_ORIGINAL_DRAFT,
          JSON.stringify({ subject, bodyHtml, attachmentNames }),
        );

        // 2. Mutate compose item
        if (mode === "pdfOnly") {
          await _preparePdfOnly(password);
        } else {
          await _prepareFull(password, keepSubjectUnencrypted);
        }

        // 3. Record the encrypted subject so we can detect compose item changes
        const encryptedSubject = await getSubject();
        sessionStorage.setItem(
          SS_ORIGINAL_DRAFT,
          JSON.stringify({ subject, bodyHtml, attachmentNames, encryptedSubject }),
        );

        // 4. Mark as prepared (localStorage for commands.js, state for UI)
        localStorage.setItem(LS_MAIL_PREPARED, "1");
        setPreparedInSeconds(Math.round((Date.now() - startTime) / 1000));
        setIsPrepared(true);
      } finally {
        setIsPreparing(false);
      }
    },
    [_prepareFull, _preparePdfOnly],
  );

  // ── Public: restoreDraft ───────────────────────────────────────────────────

  /**
   * Restores the original subject and body from the sessionStorage snapshot.
   * Also removes all current non-inline attachments (encrypted artifacts added
   * during preparation). Original attachments cannot be restored — returns a
   * warning string if any were present so the caller can show it.
   *
   * @returns {{ attachmentWarning: string|null }}
   */
  const restoreDraft = useCallback(async () => {
    const raw = sessionStorage.getItem(SS_ORIGINAL_DRAFT);
    if (!raw) {
      localStorage.removeItem(LS_MAIL_PREPARED);
      setIsPrepared(false);
      return { attachmentWarning: null };
    }

    const { subject, bodyHtml, attachmentNames } = JSON.parse(raw);

    // Remove all current non-inline attachments (encrypted artifacts)
    const currentItems = await getAttachmentItems();
    for (const item of currentItems) {
      if (!item.isInline) {
        await removeAttachment(item.id);
      }
    }

    await setSubject(subject);
    await setBody(bodyHtml);

    localStorage.removeItem(LS_MAIL_PREPARED);
    sessionStorage.removeItem(SS_ORIGINAL_DRAFT);
    setIsPrepared(false);

    const attachmentWarning =
      attachmentNames.length > 0
        ? t("Betreff und Text wurden wiederhergestellt. Die folgenden Anhänge müssen manuell erneut hinzugefügt werden: {names}", {
            names: attachmentNames.join(", "),
          })
        : null;

    return { attachmentWarning };
  }, [t]);

  // Called periodically by PasswordManager to detect compose item changes.
  // If the current subject no longer matches the encrypted subject stored in
  // the snapshot, we are on a different compose item — clear stale state.
  //
  // Known limitation (edge case): if the user opens a different draft whose
  // subject happens to match the encrypted subject of the previously prepared
  // mail (e.g. both are "Verschlüsselte E-Mail"), checkIfStale will NOT clear
  // the snapshot and "Verschlüsselung rückgängig machen" will remain visible
  // even though no restorable snapshot exists for that draft. Pressing the
  // button in this case is harmless — restoreDraft detects the missing snapshot
  // and only clears the flag without touching the compose item.
  const checkIfStale = useCallback(async () => {
    const raw = sessionStorage.getItem(SS_ORIGINAL_DRAFT);
    if (!raw) return;
    let snapshot;
    try { snapshot = JSON.parse(raw); } catch { return; }
    if (!snapshot.encryptedSubject) return;
    const currentSubject = await getSubject();
    if (currentSubject !== snapshot.encryptedSubject) {
      localStorage.removeItem(LS_MAIL_PREPARED);
      sessionStorage.removeItem(SS_ORIGINAL_DRAFT);
      setIsPrepared(false);
    }
  }, []);

  return {
    isPrepared,
    isPreparing,
    prepareEncryption,
    restoreDraft,
    checkIfStale,
    preparedInSeconds,
  };
}
